import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PageWrapper } from '../components/layout/PageWrapper';
import { AuthGuard } from '../components/auth/AuthGuard';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useNextGameAvailability, useCreatePaymentIntent, useMyNextGameReservation, useCancelReservation } from '../hooks/useReservation';
import { useCountdown, formatCountdown } from '../hooks/useCountdown';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import { Seo } from '../components/Seo';

declare global {
  interface Window { __STRIPE_PK__?: string; }
}

const stripeAppearance = {
  theme: 'night' as const,
  variables: {
    colorBackground: '#171410',
    colorText: '#e8dcc8',
    colorTextSecondary: '#a89880',
    colorPrimary: '#c9a237',
    colorDanger: '#8b1a1a',
    colorIconTab: '#a89880',
    colorIconTabSelected: '#c9a237',
    borderRadius: '0px',
    fontSizeBase: '14px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#0f0e0c',
      border: '1px solid #2a2520',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #c9a237',
      boxShadow: '0 0 0 1px #c9a237',
    },
    '.Label': {
      color: '#c9a237',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      fontSize: '11px',
    },
    '.Tab': {
      backgroundColor: '#171410',
      border: '1px solid #2a2520',
    },
    '.Tab--selected': {
      border: '1px solid #c9a237',
      backgroundColor: '#0f0e0c',
    },
    '.Tab--selected .TabLabel': {
      color: '#c9a237',
    },
    '.Error': {
      color: '#b02222',
    },
  },
};

function PaymentForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('reserve');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/reserve?success=1` },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? t('payment.error'));
      setProcessing(false);
    } else {
      onSuccess();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-mafia-accent text-white py-3 rounded-xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? t('payment.processing') : t('payment.pay')}
      </button>
    </form>
  );
}

function AlreadyBooked({
  reservationId,
  gameDate,
  status,
  isFree = false,
}: {
  reservationId: string;
  gameDate: string;
  status: string;
  isFree?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const cancel = useCancelReservation();

  const dateStr = new Date(gameDate + 'T00:00:00').toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const isConfirmed = status === 'CONFIRMED';

  // Free admins can cancel anytime; regular players must cancel 3+ days before
  const deadlineMs = new Date(gameDate + 'T00:00:00Z').getTime() - 3 * 24 * 60 * 60 * 1000;
  const canCancel = isFree || Date.now() < deadlineMs;
  const deadlineStr = new Date(deadlineMs).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long',
  });

  async function handleCancel() {
    setCancelError('');
    try {
      await cancel.mutateAsync(reservationId);
    } catch (err: unknown) {
      setCancelError((err as Error).message || 'Не удалось отменить бронирование.');
      setConfirming(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto text-center">
      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
        ◆ Бронирование ◆
      </p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">
        Вы уже записаны
      </h1>

      <OrnamentDivider className="mb-8" />

      <div className="card-vintage p-8 text-left space-y-4">
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">Дата игры</p>
          <p className="text-mafia-paper font-display text-xl">{dateStr}</p>
        </div>
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">Место</p>
          <p className="text-mafia-paper">Banyo Library · 284 St Vincents Rd · 18:00</p>
        </div>
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">Статус</p>
          {isConfirmed ? (
            <p className="text-emerald-400 font-mono text-sm">Подтверждено — ждём вас за столом</p>
          ) : (
            <p className="text-amber-400 font-mono text-sm">Ожидание подтверждения оплаты</p>
          )}
        </div>

        {/* Cancel section */}
        {canCancel && (
          <div className="pt-4 border-t border-mafia-border">
            {!confirming ? (
              <div>
                <button
                  onClick={() => setConfirming(true)}
                  className="text-mafia-muted hover:text-mafia-paper-dim text-xs font-mono underline underline-offset-2 transition-colors"
                >
                  Отменить бронирование
                </button>
                {!isFree && (
                  <p className="text-mafia-muted text-xs font-mono mt-1">
                    Возврат средств доступен до {deadlineStr}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-mafia-paper text-sm font-mono">
                  {isFree
                    ? 'Вы уверены? Бронирование будет отменено.'
                    : 'Вы уверены? Оплата будет возвращена на вашу карту.'}
                </p>
                {cancelError && (
                  <p className="text-amber-400 text-xs font-mono">{cancelError}</p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={handleCancel}
                    disabled={cancel.isPending}
                    className="btn-crimson flex-1 py-2 text-xs disabled:opacity-50"
                  >
                    {cancel.isPending ? 'Отменяем...' : 'Да, отменить'}
                  </button>
                  <button
                    onClick={() => { setConfirming(false); setCancelError(''); }}
                    className="flex-1 py-2 text-xs font-mono border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors"
                  >
                    Нет, оставить
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!canCancel && isConfirmed && (
          <p className="text-mafia-muted text-xs font-mono pt-4 border-t border-mafia-border">
            Отмена недоступна — до игры менее 3 дней
          </p>
        )}
      </div>
    </div>
  );
}

function NotOpenYet({ opensAt }: { opensAt: string }) {
  const { days, hours, minutes, seconds, expired } = useCountdown(opensAt);
  const dt = new Date(opensAt);
  const dateStr = dt.toLocaleDateString('ru-RU', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Australia/Brisbane',
  });
  const timeStr = dt.toLocaleTimeString('ru-RU', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Brisbane',
  });

  return (
    <div className="max-w-lg mx-auto text-center">
      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
        ◆ Бронирование ◆
      </p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">
        Ещё не открыто
      </h1>

      <OrnamentDivider className="mb-8" />

      <div className="card-vintage p-8 space-y-4 text-center">
        <p className="text-mafia-paper-dim font-mono text-sm">
          Бронирование мест откроется
        </p>
        <p className="font-display text-2xl text-mafia-gold font-bold capitalize">
          {dateStr}
        </p>
        <p className="font-mono text-mafia-paper text-lg tracking-widest">
          {timeStr} <span className="text-mafia-muted text-xs">AEST</span>
        </p>
        {!expired && (
          <div className="pt-3 border-t border-mafia-border">
            <p className="text-mafia-muted text-xs font-mono uppercase tracking-widest mb-1">До открытия</p>
            <p className="font-mono text-mafia-gold text-xl tracking-widest">
              {formatCountdown(days, hours, minutes, seconds)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function RefundedState({
  gameDate,
  isFree = false,
  onRebook,
}: {
  gameDate: string;
  isFree?: boolean;
  onRebook?: () => void;
}) {
  const dateStr = new Date(gameDate + 'T00:00:00').toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="max-w-lg mx-auto text-center">
      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
        ◆ Бронирование ◆
      </p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">
        {isFree ? 'Бронирование отменено' : 'Возврат оформлен'}
      </h1>

      <OrnamentDivider className="mb-8" />

      <div className="card-vintage p-8 text-left space-y-4">
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">Дата игры</p>
          <p className="text-mafia-paper font-display text-xl">{dateStr}</p>
        </div>
        <div>
          <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">Статус</p>
          <p className="text-mafia-paper-dim font-mono text-sm">
            {isFree ? 'Бронирование отменено' : 'Бронирование отменено, средства возвращены'}
          </p>
        </div>
        {isFree && onRebook ? (
          <div className="pt-4 border-t border-mafia-border">
            <button onClick={onRebook} className="btn-gold-solid w-full py-3 text-sm">
              Записаться снова
            </button>
          </div>
        ) : (
          <p className="text-mafia-paper-dim text-sm font-mono pt-4 border-t border-mafia-border">
            Ждём вас на следующей игре!
          </p>
        )}
      </div>
    </div>
  );
}

function ReserveContent({ onLoginRequired }: { onLoginRequired: () => void }) {
  const { t } = useTranslation('reserve');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const stripePromise = useMemo(() => loadStripe(window.__STRIPE_PK__ ?? ''), []);
  const { data: availability, isLoading: availLoading } = useNextGameAvailability();
  const myReservation = useMyNextGameReservation(availability?.gameDate);
  const createPaymentIntent = useCreatePaymentIntent();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const free = isFreeAdmin(user?.email);
  const pendingResume = myReservation?.status === 'PENDING' && !clientSecret;

  async function handleReserve() {
    const result = await createPaymentIntent.mutateAsync();
    if (result.free) {
      await queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      await queryClient.invalidateQueries({ queryKey: ['next-game-availability'] });
      setConfirmed(true);
    } else {
      setClientSecret(result.clientSecret);
    }
  }

  // Just reserved/paid — show success
  if (confirmed) {
    return (
      <AlreadyBooked
        reservationId={myReservation?.reservationId ?? ''}
        gameDate={availability?.gameDate ?? ''}
        status="CONFIRMED"
        isFree={free}
      />
    );
  }

  return (
    <AuthGuard onLoginRequired={onLoginRequired}>
      {availLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : myReservation?.status === 'CONFIRMED' ? (
        <AlreadyBooked
          reservationId={myReservation.reservationId}
          gameDate={myReservation.gameDate}
          status="CONFIRMED"
          isFree={free}
        />
      ) : myReservation?.status === 'CANCELLED' ? (
        <RefundedState gameDate={myReservation.gameDate} isFree={free} onRebook={free ? handleReserve : undefined} />
      ) : availability && !availability.isOpen && !free && myReservation?.status !== 'PENDING' ? (
        <NotOpenYet opensAt={availability.opensAt} />
      ) : (
        <div className="max-w-lg mx-auto">
          <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
            ◆ Бронирование ◆
          </p>
          <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">{t('title')}</h1>

          {availability && (
            <div className="card-vintage p-6 mb-6 space-y-2">
              <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-3">
                Следующая игра
              </p>
              <p className="text-mafia-paper-dim text-sm">
                {t('game.date')}:{' '}
                <span className="text-mafia-paper capitalize">
                  {new Date(availability.gameDate + 'T12:00:00').toLocaleDateString('ru-RU', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })} · 18:00
                </span>
              </p>
              <p className="text-mafia-paper-dim text-sm">
                {t('game.venue')}: <span className="text-mafia-paper">Banyo Library</span>
              </p>
              <p className="text-mafia-paper-dim text-sm">
                {t('game.spots')}:{' '}
                <span className={`font-semibold ${availability.availableSpots > 2 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {availability.availableSpots} / {availability.totalSpots}
                </span>
              </p>
              {!free && (
                <div className="pt-3 mt-1 border-t border-mafia-border flex items-baseline justify-between">
                  <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase">
                    Стоимость
                  </p>
                  <p className="font-display text-2xl font-bold text-mafia-gold">
                    ${availability.priceAud} <span className="text-sm font-mono font-normal text-mafia-paper-dim">AUD</span>
                  </p>
                </div>
              )}
              {availability.availableSpots === 0 && (
                <p className="text-mafia-crimson font-mono text-sm pt-1">{t('game.full')}</p>
              )}
            </div>
          )}

          {((availability && availability.availableSpots > 0) || pendingResume || !!clientSecret) && (
            <>
              <div className="card-vintage p-6 mb-6 space-y-3">
                <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-1">
                  Ваши данные
                </p>
                <div>
                  <p className="text-mafia-muted text-xs font-mono">{t('player.name')}</p>
                  <p className="text-mafia-paper font-medium">{user?.displayName}</p>
                </div>
                <div>
                  <p className="text-mafia-muted text-xs font-mono">{t('player.email')}</p>
                  <p className="text-mafia-paper font-medium">{user?.email}</p>
                </div>
              </div>

              {pendingResume && (
                <div className="mb-4 p-4 border border-amber-400/30 bg-amber-400/5">
                  <p className="text-amber-400 font-mono text-sm">
                    У вас есть незавершённая оплата — место зарезервировано, но ещё не оплачено.
                  </p>
                </div>
              )}
              {!clientSecret ? (
                <button
                  onClick={handleReserve}
                  disabled={createPaymentIntent.isPending}
                  className="btn-gold-solid w-full py-4 text-sm disabled:opacity-50"
                >
                  {createPaymentIntent.isPending
                    ? t('loading')
                    : free
                      ? 'Забронировать'
                      : pendingResume
                        ? `Оплатить · $${availability?.priceAud} AUD`
                        : `${t('proceedToPayment')} · $${availability?.priceAud} AUD`}
                </button>
              ) : (
                <div className="card-vintage p-6">
                  <h3 className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-4">
                    {t('payment.title')}
                  </h3>
                  <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
                    <PaymentForm onSuccess={() => setConfirmed(true)} />
                  </Elements>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AuthGuard>
  );
}

interface ReservePageProps {
  onLoginRequired: () => void;
}

export function ReservePage({ onLoginRequired }: ReservePageProps) {
  return (
    <PageWrapper>
      <Seo title="Забронировать место" description="Забронируйте место на ближайшую игру в мафию в Брисбене." path="/reserve" />
      <ReserveContent onLoginRequired={onLoginRequired} />
    </PageWrapper>
  );
}
